import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import api from "@/lib/api";
import { toast } from "sonner";
import { currency } from "@/lib/status";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { ArrowLeft, Trash2, RotateCcw, Clock } from "lucide-react";

export default function StaffTrash() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [purge, setPurge] = useState(null);

  const load = () => api.get("/requests/trash").then(({ data }) => setItems(data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const restore = async (id) => {
    setItems((prev) => prev.filter((x) => x.id !== id));
    try { await api.post(`/requests/${id}/restore`); toast.success("Deal restored to pipeline"); }
    catch { toast.error("Couldn't restore deal"); load(); }
  };
  const permanent = async (id) => {
    setPurge(null);
    setItems((prev) => prev.filter((x) => x.id !== id));
    try { await api.delete(`/requests/${id}/permanent`); toast.success("Deal permanently deleted"); }
    catch { toast.error("Couldn't delete deal"); load(); }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <Navbar />
      <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
        <button onClick={() => navigate("/staff")} className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-slate-500 dark:text-slate-400 hover:text-slate-900" data-testid="trash-back-btn"><ArrowLeft className="h-4 w-4" /> Pipeline</button>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-red-50 text-red-600"><Trash2 className="h-5 w-5" strokeWidth={2.5} /></div>
          <div>
            <h1 className="font-display text-3xl font-black tracking-tighter text-slate-950 dark:text-slate-50">Trash</h1>
            <p className="text-sm text-slate-600 dark:text-slate-300">Deleted deals are kept here. Restore them to the pipeline or delete permanently.</p>
            <p className="mt-1 inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-[11px] font-bold text-amber-700 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-400" data-testid="trash-autoclean-note"><Clock className="h-3 w-3" /> Auto-cleans after 30 days</p>
          </div>
        </div>

        {items === null ? (
          <div className="mt-8 space-y-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-20 w-full rounded-lg" />)}</div>
        ) : items.length === 0 ? (
          <div className="mt-10 rounded-xl border border-dashed border-slate-200 dark:border-slate-800 py-16 text-center" data-testid="trash-empty">
            <Trash2 className="mx-auto h-8 w-8 text-slate-300" />
            <p className="mt-3 font-semibold text-slate-500 dark:text-slate-400">Trash is empty</p>
            <p className="text-sm text-slate-400 dark:text-slate-500">Deals you delete from the pipeline show up here.</p>
          </div>
        ) : (
          <div className="mt-8 space-y-3" data-testid="trash-list">
            {items.map((r) => (
              <div key={r.id} data-testid={`trash-item-${r.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
                <div className="min-w-0">
                  <p className="truncate font-display text-base font-bold text-slate-900 dark:text-slate-100">{r.year} {r.make} {r.model || "Vehicle request"}</p>
                  <p className="truncate text-sm text-slate-500 dark:text-slate-400">{r.buyer_name || r.buyer_email} · {r.quote ? currency(r.quote.total) : r.msrp ? currency(r.msrp) : "No quote"}</p>
                  <p className="mt-0.5 text-[11px] text-slate-400 dark:text-slate-500">Deleted{r.deleted_by ? ` by ${r.deleted_by}` : ""}{r.deleted_at ? ` · ${new Date(r.deleted_at).toLocaleString()}` : ""}</p>
                </div>
                <div className="flex shrink-0 gap-2">
                  <Button variant="outline" size="sm" onClick={() => restore(r.id)} data-testid={`trash-restore-${r.id}`} className="rounded-md border-emerald-200 font-semibold text-emerald-700 hover:bg-emerald-50"><RotateCcw className="mr-1.5 h-4 w-4" /> Restore</Button>
                  <Button variant="outline" size="sm" onClick={() => setPurge(r)} data-testid={`trash-purge-${r.id}`} className="rounded-md border-red-200 font-semibold text-red-600 hover:bg-red-50"><Trash2 className="mr-1.5 h-4 w-4" /> Delete forever</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <AlertDialog open={!!purge} onOpenChange={(o) => { if (!o) setPurge(null); }}>
        <AlertDialogContent data-testid="purge-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this deal permanently?</AlertDialogTitle>
            <AlertDialogDescription>
              {purge ? `${purge.year || ""} ${purge.make || ""} ${purge.model || ""} (${purge.buyer_name || purge.buyer_email})` : ""} and all its documents will be permanently removed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="purge-cancel">Keep in Trash</AlertDialogCancel>
            <AlertDialogAction data-testid="purge-confirm" onClick={() => purge && permanent(purge.id)} className="bg-red-600 text-white hover:bg-red-700">Delete forever</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
